import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import { TooltipHost } from '@bingads-webui-react/fabric-control';

import {
  getSimplePreviewCarouselHeaderStyles,
  getPreviewCarouselHeaderWithButtonsStyles,
  getIncludeExcludeEditButtonStyles,
  getReCropReplaceButtonsStyles,
} from './styles/header';

const SimplePreviewCarouselHeader = ({
  title,
  styleOverrides,
}) => {
  const classes = useDefaultStyles(getSimplePreviewCarouselHeaderStyles({ styleOverrides }));

  return (
    <div className={classnames('ad-preview-title', classes.title)}>
      {title}
    </div>
  );
};

SimplePreviewCarouselHeader.propTypes = {
  title: PropTypes.string.isRequired,
  styleOverrides: PropTypes.shape({}),
};

SimplePreviewCarouselHeader.defaultProps = {
  styleOverrides: {},
};

const IncludeExcludeEditButtons = ({
  index,
  isIncludedImage,
  isRecommendedImage,
  getIsRequiredByIndex,
  getTemplateTypeByIndex,
  onAddAspectRatio,
  onRemoveAspectRatio,
  onEdit,
  styleOverrides,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getIncludeExcludeEditButtonStyles({ styleOverrides }));

  // eslint-disable-next-line arrow-body-style
  const isRequired = useMemo(() => {
    return getIsRequiredByIndex(index);
  }, [getIsRequiredByIndex, index]);

  // eslint-disable-next-line arrow-body-style
  const excludeButton = useMemo(() => {
    return (
      <button
        className={classnames('exclude-aspect-ratio-button btn btn-default', classes.exclude)}
        onClick={() => onRemoveAspectRatio(getTemplateTypeByIndex(index))}
        disabled={isRequired}
        data-index={index}
      >
        { i18n.getString(_TL_('Exclude')) }
      </button>
    );
  }, [index, i18n, onRemoveAspectRatio, classes, isRequired, getTemplateTypeByIndex]);

  return (
    <div className={classnames('ad-preview-actions', 'inline-title', classes.actionButtons)}>
      {!isIncludedImage ?
        (onAddAspectRatio &&
          <button
            className="include-aspect-ratio-button btn btn-default"
            onClick={() => onAddAspectRatio(getTemplateTypeByIndex(index))}
            data-index={index}
          >
            { i18n.getString(_TL_('Include')) }
          </button>
        ) :
        (
          <React.Fragment>
            {!isRecommendedImage &&
              <button
                className="edit-image-button btn btn-default"
                onClick={() => onEdit(getTemplateTypeByIndex(index))}
                data-index={index}
                title={i18n.getString(_TL_('Edit aspect ratio'))}
              >
                { i18n.getString(_TL_('Edit aspect ratio')) }
              </button>
            }
            {isRequired ?
            (onRemoveAspectRatio &&
              <TooltipHost
                content={i18n.getString(_TL_('This placement is required and cannot be excluded.'))}
                calloutProps={{ gapSpace: 8 }}
              >
                { excludeButton }
              </TooltipHost>
            )
              : (onRemoveAspectRatio && excludeButton)
            }
          </React.Fragment>
        )
      }
    </div>
  );
};

IncludeExcludeEditButtons.propTypes = {
  index: PropTypes.number.isRequired,
  isIncludedImage: PropTypes.bool.isRequired,
  isRecommendedImage: PropTypes.bool.isRequired,
  getIsRequiredByIndex: PropTypes.func.isRequired,
  onAddAspectRatio: PropTypes.func,
  onRemoveAspectRatio: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  getTemplateTypeByIndex: PropTypes.func.isRequired,
  styleOverrides: PropTypes.shape({}),
};

IncludeExcludeEditButtons.defaultProps = {
  onAddAspectRatio: undefined,
  onRemoveAspectRatio: undefined,
  styleOverrides: {},
};

const ReCropReplaceButtons = ({
  index,
  onCrop,
  onReplace,
  styleOverrides,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getReCropReplaceButtonsStyles({ styleOverrides }));

  return (
    <div>
      <button
        className={classnames('crop-image-button btn btn-default', classes.recrop)}
        onClick={() => onCrop(index)}
        data-index={index}
      >
        { i18n.getString(_TL_('Re-crop')) }
      </button>
      <button
        className={classnames('replace-image-button btn btn-default', classes.replace)}
        onClick={() => onReplace(index)}
        data-index={index}
      >
        { i18n.getString(_TL_('Replace'))}
      </button>
    </div>
  );
};

ReCropReplaceButtons.propTypes = {
  index: PropTypes.number.isRequired,
  onCrop: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  styleOverrides: PropTypes.shape({}),
};

ReCropReplaceButtons.defaultProps = {
  styleOverrides: {},
};

const PreviewCarouselHeaderWithButtons = ({
  index,
  title,
  headerButtonProps,
  selectedImage,
  styleOverrides,
  getIsRequiredByIndex,
  getTemplateTypeByIndex,
}) => {
  const classes = useDefaultStyles(getPreviewCarouselHeaderWithButtonsStyles({ styleOverrides }));
  const { onCrop } = headerButtonProps;

  return (
    <div className={classnames('preview-header', classes.headerRoot)}>
      <div className={classnames('ad-preview-title', 'with-additional-tag', classes.title)}>
        {title}
      </div>
      <div className={classnames('flex-grow', classes.buttons)} />
      { onCrop ?
        <ReCropReplaceButtons
          index={index}
          styleOverrides={styleOverrides}
          {...headerButtonProps}
        /> :
        <IncludeExcludeEditButtons
          index={index}
          isIncludedImage={selectedImage.isIncluded}
          isRecommendedImage={selectedImage.isRecommendedImage}
          styleOverrides={styleOverrides}
          getIsRequiredByIndex={getIsRequiredByIndex}
          getTemplateTypeByIndex={getTemplateTypeByIndex}
          {...headerButtonProps}
        /> }
    </div>
  );
};

PreviewCarouselHeaderWithButtons.propTypes = {
  index: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  headerButtonProps: PropTypes.shape({
    onCrop: PropTypes.func,
  }).isRequired,
  selectedImage: PropTypes.shape({}).isRequired,
  getIsRequiredByIndex: PropTypes.func.isRequired,
  getTemplateTypeByIndex: PropTypes.func.isRequired,
  styleOverrides: PropTypes.shape({}),
};

PreviewCarouselHeaderWithButtons.defaultProps = {
  styleOverrides: {},
};

export const Header = (props) => {
  const {
    index,
    getCarouselTitleByIndex,
    getIsRequiredByIndex,
    selectedImage,
    headerButtonProps,
    hideHeaderButtons,
    isImageValid,
    styleOverrides,
    getTemplateTypeByIndex,
    shouldHideHeaderButtonsByIndex,
  } = props;

  const title = useMemo(() => getCarouselTitleByIndex(index), [index, getCarouselTitleByIndex]);
  const { header: headerStyles } = styleOverrides;

  if (!hideHeaderButtons && isImageValid && headerButtonProps && !shouldHideHeaderButtonsByIndex(index)) {
    return (
      <PreviewCarouselHeaderWithButtons
        index={index}
        title={title}
        selectedImage={selectedImage}
        headerButtonProps={headerButtonProps}
        styleOverrides={headerStyles}
        getIsRequiredByIndex={getIsRequiredByIndex}
        getTemplateTypeByIndex={getTemplateTypeByIndex}
      />
    );
  }

  return (
    <SimplePreviewCarouselHeader
      title={title}
      styleOverrides={headerStyles}
    />
  );
};

Header.propTypes = {
  index: PropTypes.number.isRequired,
  selectedImage: PropTypes.shape({}).isRequired,
  getCarouselTitleByIndex: PropTypes.func.isRequired,
  getIsRequiredByIndex: PropTypes.func.isRequired,
  isImageValid: PropTypes.bool.isRequired,
  getTemplateTypeByIndex: PropTypes.func.isRequired,
  shouldHideHeaderButtonsByIndex: PropTypes.func.isRequired,
  hideHeaderButtons: PropTypes.bool,
  headerButtonProps: PropTypes.shape({}),
  styleOverrides: PropTypes.shape({
    header: PropTypes.shape({}),
  }),
};

Header.defaultProps = {
  hideHeaderButtons: false,
  headerButtonProps: null,
  styleOverrides: {},
};
